import { Facebook, Instagram, Linkedin, Mail, MapPin, Phone } from "lucide-react";
import { BRAND, BUSINESS_UNITS, LOGOS } from "@/lib/eraContent";
import { useLang } from "@/lib/LanguageContext";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - 88;
  window.scrollTo({ top, behavior: "smooth" });
};

export const Footer = () => {
  const { t } = useLang();

  const exploreLinks = [
    { id: "ecosystem", label: t.footer.links.ecosystem },
    { id: "about", label: t.footer.links.about },
    { id: "industries", label: t.footer.links.industries },
    { id: "why-era", label: t.footer.links.whyEra },
    { id: "contact", label: t.footer.links.contact },
  ];

  const resourceLinks = [
    { href: "#", label: t.footer.links.privacy, id: "privacy" },
    { href: "#", label: t.footer.links.terms, id: "terms" },
    { href: "#", label: t.footer.links.careers, id: "careers" },
  ];

  return (
    <footer
      data-testid="site-footer"
      className="relative overflow-hidden pt-24 pb-10 text-navy-700/85"
      style={{
        background:
          "linear-gradient(180deg, #F1F4FB 0%, #E4E9F5 100%)",
      }}
    >
      {/* Subtle decorative gradient blobs */}
      <div
        aria-hidden="true"
        className="absolute -top-32 -right-20 w-96 h-96 rounded-full blur-[110px] opacity-40"
        style={{ background: "radial-gradient(circle, rgba(245,184,0,0.35), transparent 70%)" }}
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-24 -left-16 w-96 h-96 rounded-full blur-[130px] opacity-40"
        style={{ background: "radial-gradient(circle, rgba(29,78,216,0.25), transparent 70%)" }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(13,27,62,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(13,27,62,0.5) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-12 lg:col-span-4">
            <div className="flex items-center">
              {/* Logo standalone — no company name text or tagline beside it */}
              <img
                src={LOGOS.group}
                alt={BRAND.fullName}
                className="h-14 w-auto object-contain"
              />
            </div>
            <p className="mt-6 text-sm text-navy-700/70 leading-relaxed max-w-md">
              {t.footer.intro}
            </p>
            <div className="mt-6">
              <div className="text-xs uppercase tracking-[0.22em] text-navy-700/60 mb-3 font-semibold">
                {t.footer.social}
              </div>
              <div className="flex items-center gap-3">
                {[
                  { icon: Linkedin, href: BRAND.socials.linkedin, label: "LinkedIn" },
                  { icon: Instagram, href: BRAND.socials.instagram, label: "Instagram" },
                  { icon: Facebook, href: BRAND.socials.facebook, label: "Facebook" },
                ].map(({ icon: Icon, href, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-social-${label.toLowerCase()}`}
                    aria-label={label}
                    className="h-10 w-10 rounded-full bg-white/60 hover:bg-navy-900 hover:text-white text-navy-900 border border-navy-100 hover:border-navy-900 backdrop-blur flex items-center justify-center transition-colors"
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="md:col-span-4 lg:col-span-2">
            <div className="text-xs uppercase tracking-[0.24em] font-semibold text-navy-900 mb-5">
              {t.footer.columns.explore}
            </div>
            <ul className="space-y-3">
              {exploreLinks.map((l) => (
                <li key={l.id}>
                  <button
                    onClick={() => scrollTo(l.id)}
                    data-testid={`footer-link-${l.id}`}
                    className="text-sm text-navy-700/70 hover:text-navy-900 transition-colors"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4 lg:col-span-3">
            <div className="text-xs uppercase tracking-[0.24em] font-semibold text-navy-900 mb-5">
              {t.footer.columns.units}
            </div>
            <ul className="space-y-4">
              {BUSINESS_UNITS.map((u) => (
                <li key={u.id}>
                  <a
                    href={u.website === "#" ? undefined : u.website}
                    target={u.website === "#" ? undefined : "_blank"}
                    rel={u.website === "#" ? undefined : "noopener noreferrer"}
                    data-testid={`footer-bu-${u.id}`}
                    className={
                      "group block " +
                      (u.website === "#" ? "cursor-default" : "")
                    }
                  >
                    <div className="text-sm text-navy-900 font-medium group-hover:text-gold-700 transition-colors">
                      {u.name}
                    </div>
                    <div className="text-xs text-navy-700/50 mt-0.5">
                      {u.website === "#"
                        ? t.ecosystem.comingSoon
                        : u.website.replace("https://", "")}
                    </div>
                  </a>
                </li>
              ))}
            </ul>
            <div className="mt-8 text-xs uppercase tracking-[0.24em] font-semibold text-navy-900 mb-4">
              {t.footer.columns.resources}
            </div>
            <ul className="space-y-3">
              {resourceLinks.map((l) => (
                <li key={l.id}>
                  <a
                    href={l.href}
                    data-testid={`footer-resource-${l.id}`}
                    className="text-sm text-navy-700/70 hover:text-navy-900 transition-colors"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4 lg:col-span-3">
            <div className="text-xs uppercase tracking-[0.24em] font-semibold text-navy-900 mb-5">
              {t.footer.columns.contact}
            </div>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-gold-700 mt-0.5 shrink-0" />
                <a
                  href={`mailto:${BRAND.email}`}
                  className="text-navy-700/80 hover:text-navy-900 break-all"
                >
                  {BRAND.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-gold-700 mt-0.5 shrink-0" />
                <a
                  href={`https://wa.me/${BRAND.phoneDigits}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-navy-700/80 hover:text-navy-900"
                >
                  {BRAND.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-gold-700 mt-0.5 shrink-0" />
                <span
                  data-testid="footer-address"
                  className="text-navy-700/80 leading-relaxed"
                >
                  {BRAND.address}
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-navy-200/60 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-navy-700/60">
            © {new Date().getFullYear()} {BRAND.fullName}. {t.footer.rights}
          </p>
          <p className="text-xs text-navy-700/60">{t.footer.made}</p>
        </div>
      </div>
    </footer>
  );
};
